import React, { useState } from "react";
import { useSelector } from "react-redux";
import { Controller } from "react-hook-form";
import { toast } from "react-toastify";
import { Form, Button, Icon, Grid, Dropdown, Input, Modal, Label } from "semantic-ui-react";
import { Storage } from 'aws-amplify';
import { verifyWhatsAppImage } from '../../webservices/activationRulesWS';

export const useWhatsAppTemplate = () => {
  const [localTemplate, setLocalTemplate] = useState(null);
  const [localInputValues, setLocalInputValues] = useState([]);
  const [headerImage, setHeaderImage] = useState(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [mediaId, setMediaId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const authData = useSelector(state => state.authData);

  const hasImageHeader = (template) => {
    return template?.category === 'MARKETING' &&
      template?.components?.some(c => c.type === 'HEADER' && c.format === 'IMAGE');
  };

  const extractUniquePlaceholders = (text) => {
    const regex = /{{(\d+)}}/g;
    const placeholders = [];
    let match;
    while ((match = regex.exec(text)) !== null) {
      if (!placeholders.includes(match[1])) {
        placeholders.push(match[1]);
      }
    }
    return placeholders;
  };

  const handlePlaceholderChange = (index, value) => {
    const newLocalInputValues = [...localInputValues];
    newLocalInputValues[index] = { placeholder: `${index + 1}`, value: value };
    setLocalInputValues(newLocalInputValues);
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setHeaderImage(file);
      setMediaId(null);
    }
  };

  const handleVerifyImage = async (tenantId, s3Folder) => {
    if (!headerImage) {
      toast.error("Please select an image first");
      return;
    }

    setIsVerifying(true);
    try {
      const imageId = `${Date.now()}`;
      const s3Path = `${process.env.REACT_APP_ENV}/${s3Folder}/${tenantId}/${imageId}`;

      await Storage.put(s3Path, headerImage, {
        level: 'public',
        contentType: headerImage.type
      });

      const pathWithPublic = `public/` + s3Path;

      const getState = () => ({ authData });
      const response = await verifyWhatsAppImage({ imageUrl: pathWithPublic }, getState);

      if (response?.media_id) {
        setMediaId(response.media_id);
        toast.success("Image verified successfully");
      } else {
        throw new Error("No mediaId returned from API");
      }

    } catch (error) {
      console.error("Error verifying image:", error);
      toast.error("Failed to verify image. Please try again");
    } finally {
      setIsVerifying(false);
    }
  };

  const handleTemplateSelection = (template) => {
    if (template == null) {
      return null;
    }
    setLocalTemplate(template);
    setIsModalOpen(true);
  };

  const confirmTemplateSelection = (onConfirm) => {
    if (hasImageHeader(localTemplate) && headerImage && !mediaId) {
      toast.error("Please verify the image before selecting the template");
      return;
    }

    const result = {
      template: localTemplate,
      placeholderValues: localInputValues,
      headerImage: headerImage,
      mediaId: mediaId
    };

    onConfirm(result);
    
    setLocalInputValues([]);
    setHeaderImage(null);
    setMediaId(null);
    setIsModalOpen(false);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const addMediaIdToTemplate = (template, mediaIdValue) => {
    if (!template || !mediaIdValue) return template;
    
    return {
      ...template,
      components: template.components.map(comp =>
        comp.type === 'HEADER' && comp.format === 'IMAGE'
          ? { ...comp, mediaId: mediaIdValue }
          : comp
      )
    };
  };

  return {
    localTemplate,
    localInputValues,
    headerImage,
    isVerifying,
    mediaId,
    isModalOpen,
    hasImageHeader,
    extractUniquePlaceholders,
    handlePlaceholderChange,
    handleImageUpload,
    handleVerifyImage,
    handleTemplateSelection,
    confirmTemplateSelection,
    closeModal,
    addMediaIdToTemplate
  };
};

export const WhatsAppTemplateModal = ({
  isOpen,
  template,
  headerImage,
  mediaId,
  isVerifying,
  onClose,
  onConfirm,
  onImageUpload,
  onVerifyImage,
  onPlaceholderChange,
  extractPlaceholders,
  hasImageHeader
}) => {
  const renderInputFields = (text) => {
    const placeholders = extractPlaceholders(text);
    return placeholders.map((placeholder, index) => (
      <div key={index}>
        <Input
          className="full-width-field"
          placeholder={`Enter value for placeholder ${placeholder}`}
          onChange={(event) => onPlaceholderChange(index, event.target.value)}
        />
        <br />
      </div>
    ));
  };

  if (!template) return null;

  return (
    <Modal open={isOpen} onClose={onClose} size="large">
      <Modal.Header>WhatsApp Template: {template.name}</Modal.Header>
      <Modal.Content>
        {hasImageHeader(template) && (
          <div style={{ marginBottom: '20px' }}>
            <Form.Field>
              <label>Header Image</label>
              <Input
                type="file"
                accept="image/*"
                onChange={onImageUpload}
                disabled={isVerifying}
              />
              {headerImage && !mediaId && (
                <div style={{ marginTop: '10px' }}>
                  <Label color="orange">
                    <Icon name="warning sign" />
                    {headerImage.name} - Not verified
                  </Label>
                  <Button
                    size="small"
                    color="blue"
                    onClick={onVerifyImage}
                    loading={isVerifying}
                    disabled={isVerifying}
                    style={{ marginLeft: '10px' }}
                  >
                    <Icon name="check circle" />
                    Verify Image
                  </Button>
                </div>
              )}
              {mediaId && (
                <Label color="green" style={{ marginTop: '10px' }}>
                  <Icon name="check" />
                  {headerImage.name} - Verified (Media ID: {mediaId})
                </Label>
              )}
            </Form.Field>
          </div>
        )}
        {template.components.map((component, index) => (
          component.type === 'BODY' && (
            <div key={index}>
              <textarea rows={10} style={{ width: '100%' }} readOnly>
                {component.text}
              </textarea>
              {renderInputFields(component.text)}
            </div>
          )
        ))}
      </Modal.Content>
      <Modal.Actions>
        <Button.Group floated="right" style={{ paddingBottom: "10px" }}>
          <Button onClick={onClose} className="danger">
            <Icon name="remove" />
            Close
          </Button>
          <Button
            onClick={onConfirm}
            className="success"
            disabled={hasImageHeader(template) && (!headerImage || !mediaId)}
          >
            <Icon name="checkmark" />
            Select Template
          </Button>
        </Button.Group>
      </Modal.Actions>
    </Modal>
  );
};

export const WhatsAppTemplateField = ({
  control,
  templates,
  selectedTemplate,
  placeholderValues,
  headerImage,
  onTemplateSelect,
  onPlaceholderChange
}) => {
  const templatesOptions = [
    { key: 'none', value: null, text: 'Select WhatsApp Template' },
    ...templates
      .map((template) => ({
        key: template.id,
        value: template,
        text: template.name,
      }))
      .sort((a, b) => a.text.localeCompare(b.text)),
  ];

  const defaultValue = selectedTemplate || "";

  return (
    <Form.Field className="full-width-field">
      <label>WhatsApp Template</label>
      <Grid>
        <Grid.Row>
          <Grid.Column>
            <Controller
              name="template"
              control={control}
              rules={{ required: "* Please select a template" }}
              defaultValue={defaultValue}
              render={({ field, fieldState }) => (
                <div>
                  <Dropdown
                    className={fieldState.invalid ? "invalid-input-field" : ""}
                    {...field}
                    placeholder="Select WhatsApp Template"
                    fluid
                    search
                    selection
                    options={templatesOptions}
                    value={defaultValue}
                    onChange={(e, { value }) => {
                      field.onChange(value);
                      onTemplateSelect(e, { value });
                    }}
                  />
                  {fieldState.invalid && (
                    <p className="validation-error">{fieldState.error.message}</p>
                  )}
                </div>
              )}
            />
          </Grid.Column>
        </Grid.Row>
        {selectedTemplate && placeholderValues && placeholderValues.length > 0 && (
          <Grid.Row>
            <Grid.Column>
              <Form.Field>
                <label>Placeholder Values For Selected Template:</label>
              </Form.Field>
              {placeholderValues.map((inputValue, index) => (
                <div key={index} style={{ marginBottom: '10px' }}>
                  <Input
                    label={`Placeholder: ${inputValue.placeholder}`}
                    value={inputValue.value}
                    placeholder={inputValue.placeholder}
                    onChange={event => onPlaceholderChange(index, event.target.value)}
                  />
                </div>
              ))}
            </Grid.Column>
          </Grid.Row>
        )}
        {selectedTemplate && headerImage && (
          <Grid.Row>
            <Grid.Column>
              <Form.Field>
                <label>Header Image:</label>
                <Label color="blue">
                  <Icon name="image" />
                  {headerImage.name}
                </Label>
              </Form.Field>
            </Grid.Column>
          </Grid.Row>
        )}
      </Grid>
    </Form.Field>
  );
};
